import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { PageTitle, CARD, INPUT, BTN, BTN_GHOST, useList } from "@/pages/admin/ui";
import { Plus, Pencil, Trash2, Film } from "lucide-react";

const CATEGORIES = ["Food", "Catering", "Events", "Chefs", "Service", "Venues", "Experiences"];
const EMPTY = { media_type: "image", url: "", category: "Food", label: "", order: 0, is_active: true, is_featured: false };

export default function GalleryAdmin() {
  const { data, loading, reload } = useList("/admin/gallery", []);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const openNew = () => { setForm({ ...EMPTY, order: data.length }); setOpen(true); };
  const openEdit = (row) => { setForm({ ...row }); setOpen(true); };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data: res } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const full = res.url.startsWith("http") ? res.url : `${process.env.REACT_APP_BACKEND_URL}${res.url}`;
      const isVideo = /\.(mp4|webm|mov|m4v)$/i.test(res.path || file.name);
      setForm((f) => ({ ...f, url: full, media_type: isVideo ? "video" : "image" }));
      toast.success("Media uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.url) { toast.error("Please upload or paste a media URL"); return; }
    setSaving(true);
    const payload = { ...form, order: Number(form.order || 0) };
    try {
      if (form.id) await api.put(`/admin/gallery/${form.id}`, payload);
      else await api.post("/admin/gallery", payload);
      toast.success("Gallery updated — live on the website");
      setOpen(false); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (row) => { if (!window.confirm("Delete this item?")) return; await api.delete(`/admin/gallery/${row.id}`); toast.success("Deleted"); reload(); };

  return (
    <div data-testid="admin-gallery">
      <PageTitle title="Gallery" subtitle="Upload images or videos. Changes appear on the public Gallery page instantly." right={<button className={BTN} onClick={openNew} data-testid="gallery-add"><Plus className="h-4 w-4" /> Add Media</button>} />
      {loading ? <div className="text-sm text-[#64748B]">Loading…</div> : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="gallery-admin-grid">
          {data.map((g) => (
            <div key={g.id} className={`${CARD} overflow-hidden`}>
              <div className="relative aspect-[4/3] bg-[#F1F5F9]">
                {g.media_type === "video"
                  ? <video src={g.url} muted className="h-full w-full object-cover" />
                  : <img src={g.url} alt={g.label} className="h-full w-full object-cover" />}
                {g.media_type === "video" && <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded bg-black/60 px-2 py-0.5 text-[10px] text-white"><Film className="h-3 w-3" /> Video</span>}
                {g.is_featured && <span className="absolute bottom-2 left-2 rounded bg-[#C5A059] px-2 py-0.5 text-[10px] font-semibold text-white">★ Featured</span>}
                {!g.is_active && <span className="absolute right-2 top-2 rounded bg-red-500 px-2 py-0.5 text-[10px] text-white">Hidden</span>}
              </div>
              <div className="p-3">
                <div className="truncate text-sm font-medium text-[#0F172A]">{g.label || "Untitled"}</div>
                <div className="text-xs text-[#64748B]">{g.category}</div>
                <div className="mt-2 flex gap-2">
                  <button className={BTN_GHOST + " !p-1.5"} onClick={() => openEdit(g)} data-testid="gallery-edit"><Pencil className="h-3.5 w-3.5" /></button>
                  <button className={BTN_GHOST + " !p-1.5 text-red-600"} onClick={() => remove(g)} data-testid="gallery-delete"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
          {data.length === 0 && <p className="col-span-full py-10 text-center text-[#94A3B8]">No media yet.</p>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">{form?.id ? "Edit" : "Add"} Gallery Media</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4" data-testid="gallery-form">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Media (image or video)</label>
                <div className="mt-1 flex items-center gap-3">
                  {form.url && (form.media_type === "video"
                    ? <video src={form.url} muted className="h-16 w-24 rounded object-cover border border-[#E2E8F0]" />
                    : <img src={form.url} alt="" className="h-16 w-24 rounded object-cover border border-[#E2E8F0]" />)}
                  <label className="cursor-pointer rounded-md border border-dashed border-[#C5A059] px-4 py-2 text-sm font-medium text-[#C5A059] hover:bg-[#C5A059]/5">
                    {uploading ? "Uploading…" : "Upload File"}
                    <input type="file" accept="image/*,video/*" className="hidden" data-testid="gallery-upload" onChange={(e) => upload(e.target.files?.[0])} />
                  </label>
                </div>
                <input className={INPUT + " mt-2"} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="…or paste a media URL" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Media Type</label>
                  <Select value={form.media_type} onValueChange={(v) => setForm({ ...form, media_type: v })}>
                    <SelectTrigger className={INPUT}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="image">Image</SelectItem><SelectItem value="video">Video</SelectItem></SelectContent>
                  </Select></div>
                <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Category</label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className={INPUT} data-testid="gallery-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Label</label>
                <input className={INPUT} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="gallery-label" /></div>
              <div className="flex items-center gap-6">
                <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Order</label>
                  <input type="number" className={INPUT + " w-24"} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
                <label className="flex items-center gap-2 pt-5 text-sm text-[#64748B]"><Switch checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /> Visible</label>
                <label className="flex items-center gap-2 pt-5 text-sm text-[#64748B]"><Switch checked={!!form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} data-testid="gallery-featured" /> Featured (home page)</label>
              </div>
              <div className="flex justify-end gap-2 pt-2"><button type="button" className={BTN_GHOST} onClick={() => setOpen(false)}>Cancel</button>
                <button type="submit" className={BTN} disabled={saving} data-testid="gallery-save">{saving ? "Saving…" : "Save"}</button></div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
